import React from 'react';
import { Search, X, Users } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { Band } from '../../types/database';
import { BandSelect } from './BandSelect';

interface FilterPanelProps {
  searchQuery: string;
  onSearchChange: (query: string) => void;
  selectedBand: string | null;
  onSelectBand: (bandId: string | null) => void;
  bands: Band[];
  onClearFilters: () => void;
}

export function FilterPanel({
  searchQuery,
  onSearchChange,
  selectedBand,
  onSelectBand,
  bands,
  onClearFilters,
}: FilterPanelProps) {
  const { t } = useTranslation();
  
  const selectedBandName = selectedBand 
    ? bands.find(b => b.id === selectedBand)?.name 
    : null;

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onSearchChange(e.target.value);
  };

  return (
    <div className="max-w-4xl mx-auto mb-12">
      <div className="text-xs text-gray-400 mb-2">{t('filters.title')}</div>
      <div className="bg-[#1a1a1a] rounded-xl p-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-light/40" />
            <input
              type="text"
              value={searchQuery}
              onChange={handleSearchChange}
              placeholder={t('stories.searchPlaceholder')}
              className="w-full pl-10 pr-4 py-2 bg-[#232323] border border-gray-custom/20 rounded-lg text-light placeholder-light/40 focus:outline-none focus:border-gray-custom/40 transition-colors"
            />
          </div>

          <BandSelect
            bands={bands}
            selectedBand={selectedBand}
            onSelectBand={onSelectBand}
          />
        </div>

        {(searchQuery || selectedBand) && (
          <div className="mt-4 flex items-center justify-between">
            <div className="flex flex-wrap gap-2">
              {searchQuery && (
                <button
                  onClick={() => onSearchChange('')}
                  className="px-3 py-1.5 rounded-full text-sm bg-primary text-light flex items-center space-x-2 hover:bg-primary/80 transition-colors"
                >
                  <span>"{searchQuery}"</span>
                  <X className="w-3 h-3" />
                </button>
              )}
              {selectedBand && (
                <button
                  onClick={() => onSelectBand(null)}
                  className="px-3 py-1.5 rounded-full text-sm bg-primary text-light flex items-center space-x-2 hover:bg-primary/80 transition-colors"
                >
                  <Users className="w-3 h-3" />
                  <span>{selectedBandName}</span>
                  <X className="w-3 h-3" />
                </button>
              )}
            </div>
            <button
              onClick={onClearFilters}
              className="ml-4 px-4 py-2 text-sm text-light/60 hover:text-light flex items-center space-x-2 bg-[#232323] rounded-lg hover:bg-[#2a2a2a] transition-colors"
            >
              <X className="w-4 h-4" />
              <span>{t('stories.clearFilters')}</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}